import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  H1,
  Button,
  Page,
  Text,
} from 'rf-ui';

import { RemoteData, NotAuthorized } from 'rf-comp';
import { get, isAuthorized } from 'rf-lib';
import * as a from './actions';
import styled from 'styled-components';
import { calculateBalance } from 'rf-api/transactions';

const TicketList = styled.ul`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
`

const TicketContainer = styled.div`
    border: 1px solid lightgrey;
    width: 20rem;
    margin: 0.5rem;
    padding: 0.5rem;
    
`;

const ControlsContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-left: 0.5rem;
`;

const InnerControlsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    white-space: nowrap;
    width: 1rem;
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0;
    padding: 0;
    background: ${props => props.background ? get('colorBackgroundSecondary') : ''};
`;

const Col = styled.div`
    flex: ${props => props.size};
    margin: 0.4rem;
    align-items: ${props => props.alignItems};
    justify-content: ${props => props.justifyContent};
`;

const Tickets = ({
  gameId,
  playerName,
  playerId,
  ticketCount,
  handleBuy,
  handleSell,
  handleSetFree,
  balance,
  cost
}) => {
  return (
    <TicketContainer>
      <Row>
        <Col alignItems="flex-start" justifyContent="flex-start">
          <Text>{`${playerName}`}</Text>
        </Col>
        <Col alignItems="flex-end" justifyContent="flex-end">
          <Text>{`Saldo: ${balance} kr`}</Text>
        </Col>
      </Row>
      <Row>
        <Col size={2}>
          <ControlsContainer>
            <Button title="-"
                    disabled={!ticketCount || ticketCount <= 0}
                    onClick={() => handleSell(gameId, playerId)}/>
            <InnerControlsContainer>
              <Text>
                {ticketCount || 0} lodd
              </Text>
              <Text>{cost},-</Text>
            </InnerControlsContainer>

            <Button title="+"
                    onClick={() => handleBuy(gameId, playerId)}/>
            <Button title="Gjør gratis"
                    onClick={() => handleSetFree(gameId, playerId)}/>
          </ControlsContainer>
        </Col>
      </Row>
    </TicketContainer>
  );
};

Tickets.propTypes = {
  gameId: PropTypes.string.isRequired,
  playerId: PropTypes.string.isRequired,
  playerName: PropTypes.string.isRequired,
  ticketCount: PropTypes.number.isRequired,
  cost: PropTypes.string.isRequired,
  handleBuy: PropTypes.func.isRequired,
  handleSell: PropTypes.func.isRequired,
  handleSetFree: PropTypes.func.isRequired,
  balance: PropTypes.number.isRequired
};

const PlayerList = ({
  game,
  group,
  tickets,
  transactionsState,
  handleBuy,
  handleSell,
  handleSetFree,
}) => {

  return (
    <>
      <H1>Administrer lodd</H1>
      <h2>{game.name}</h2>
      <TicketList>
        {(group?.players || []).map(player => {
          const ticketCount = tickets?.[player.id] ?? 0;

          const transactionsForPlayer = transactionsState?.data?.[player.id] || {};
          const playerBalance = (transactionsForPlayer && calculateBalance(transactionsForPlayer)?.amount) || 0;
          const cost = transactionsForPlayer?.[game.uid]?.amount || 0;

          return (
            <Tickets
              key={player.id}
              gameId={game.uid}
              playerId={player.id}
              playerName={player.displayName}
              handleBuy={handleBuy}
              handleSell={handleSell}
              handleSetFree={handleSetFree}
              ticketCount={ticketCount}
              balance={playerBalance}
              cost={cost}
            ></Tickets>

          );
        })}
      </TicketList>

    </>
  );
};

PlayerList.propTypes = {
  game: PropTypes.object.isRequired,
  tickets: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  transactionsState: PropTypes.object.isRequired,
  handleBuy: PropTypes.func.isRequired,
  handleSell: PropTypes.func.isRequired,
  handleSetFree: PropTypes.func.isRequired,
};

const GroupSection = ({
  game,
  fetchGroupDetails,
  addTicketsListener,
  removeTicketsListener,
  addTransactionListener,
  removeTransactionListener,
  ...props
}) => {

  if (!isAuthorized('admin', game.groupID, props.authUser)) {
    return <NotAuthorized message="You are not authorized to edit this game" />;
  }
  useEffect(() => {
    fetchGroupDetails(game.groupID);
    addTransactionListener(game.groupID);
    addTicketsListener(game.uid);
    return () => {
      removeTransactionListener(game.groupID);
      removeTicketsListener(game.uid);
    };
  }, [game.groupID]);

  return (
    <div>
      <RemoteData
        type={props.groupDetails}
        loading={() => <span>Laster spill...</span>}
        failure={err => <span>Feil: {err.message}</span>}
        success={group =>
          (
            <PlayerList
              game={game}
              tickets={props.tickets}
              group={group}
              handleBuy={props.handleBuy}
              handleSell={props.handleSell}
              handleSetFree={props.handleSetFree}
              transactionsState={props.transactionsState}
              authUser={props.authUser}
            />
          )}
      />
    </div>
  );
};

GroupSection.propTypes = {
  authUser: PropTypes.object,
  handleBuy: PropTypes.func.isRequired,
  handleSell: PropTypes.func.isRequired,
  handleSetFree: PropTypes.func.isRequired,
  removeTicketsListener: PropTypes.func.isRequired,
  addTicketsListener: PropTypes.func.isRequired,
  tickets: PropTypes.object.isRequired,
  groupDetails: PropTypes.object.isRequired,
  fetchGroupDetails: PropTypes.func.isRequired,
  transactionsState: PropTypes.object.isRequired,
  addTransactionListener: PropTypes.func.isRequired,
  removeTransactionListener: PropTypes.func.isRequired,
  game: PropTypes.object.isRequired
};

const TicketsAdminPage = ({
  fetchGame,
  fetchGroupDetails,
  addTicketsListener,
  removeTicketsListener,
  addTransactionListener,
  removeTransactionListener,
  ...props
}) => {

  useEffect(() => {
    fetchGame(props.match.params.id);
  }, []);

  return (
    <Page>
      <RemoteData
        type={props.currentGame}
        loading={() => <span>Laster spill...</span>}
        failure={err => <span>Feil: {err.message}</span>}
        success={(game) =>
          (
            <GroupSection
              authUser={props.authUser}
              game={game}
              handleBuy={props.handleBuy}
              handleSell={props.handleSell}
              handleSetFree={props.handleSetFree}
              addTicketsListener={addTicketsListener}
              removeTicketsListener={removeTicketsListener}
              addTransactionListener={addTransactionListener}
              removeTransactionListener={removeTransactionListener}
              tickets={props.tickets}
              groupDetails={props.groupDetails}
              fetchGroupDetails={fetchGroupDetails}
              transactionsState={props.transactionsState}
            />
          )}
      />
    </Page>
  );
};

TicketsAdminPage.propTypes = {
  authUser: PropTypes.object,
  fetchGame: PropTypes.func.isRequired,
  handleBuy: PropTypes.func.isRequired,
  handleSell: PropTypes.func.isRequired,
  handleSetFree: PropTypes.func.isRequired,
  removeTicketsListener: PropTypes.func.isRequired,
  addTicketsListener: PropTypes.func.isRequired,
  currentGame: PropTypes.object.isRequired,
  tickets: PropTypes.object.isRequired,
  match: PropTypes.object,
  groupDetails: PropTypes.object.isRequired,
  fetchGroupDetails: PropTypes.func.isRequired,
  transactionsState: PropTypes.object.isRequired,
  addTransactionListener: PropTypes.func.isRequired,
  removeTransactionListener: PropTypes.func.isRequired,
};

const mapStateToProps = ({
  session,
  games
}) => ({
  authUser: session.authUser,
  currentGame: games.fetchedGame,
  tickets: games.ticketsState,
  groupDetails: games.groupDetailsRemoteData,
  transactionsState: games.transactionsState
});

const mapDispatchToProps = dispatch => ({
  fetchGame: a.fetchGame(dispatch),
  resetUpdateState: a.resetUpdateState(dispatch),
  addTicketsListener: a.addTicketsListener(dispatch),
  removeTicketsListener: a.removeTicketsListener(dispatch),
  addTransactionListener: a.addTransactionListener(dispatch),
  removeTransactionListener: a.removeTransactionListener(dispatch),
  fetchGroupDetails: a.fetchGroupDetails(dispatch),
  handleBuy: a.handleBuy(dispatch),
  handleSell: a.handleSell(dispatch),
  handleSetFree: a.handleSetFree(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketsAdminPage);
